import { db } from './config';
import {
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  addDoc,
  Timestamp,
} from 'firebase/firestore';

/**
 * Crea un nuevo documento en Firestore en una colección sin ID específico (automático).
 * @param {string} collectionName - El nombre de la colección.
 * @param {object} data - Los datos que quieres guardar.
 * @param {object|null} converter - (opcional) Convertidor para transformar los datos.
 * @returns {Promise<string>} El ID del documento creado.
 */
export const addDocument = async (collectionName, data, converter = null) => {
  const colRef = converter
    ? collection(db, collectionName).withConverter(converter)
    : collection(db, collectionName);
  return await addDoc(colRef, {
    ...data,
    createdAt: Timestamp.now(),
  });
  //return docRef.id; // Devuelve el ID del documento creado
};

/**
 * Actualiza un documento existente en Firestore.
 * @param {string} collectionName - El nombre de la colección.
 * @param {string} id - El ID del documento a actualizar.
 * @param {object} updates - Los campos a actualizar.
 */
export const updateDocument = async (collectionName, id, updates) => {
  const docRef = doc(db, collectionName, id);
  return updateDoc(docRef, { ...updates, updatedAt: Timestamp.now() });
};

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Actualiza múltiples documentos en Firestore con una pausa entre cada uno.
 * @param {string} collectionName - El nombre de la colección.
 * @param {Array} docs - Array de objetos que contienen el ID y los datos a actualizar.
 * @param {string} docs[].id - El ID del documento a actualizar.
 * @param {object} docs[].data - Los datos a actualizar.
 * @param {number} delay - (opcional) Tiempo en ms entre cada actualización (por defecto 900ms)
 */
export async function updateMultiple(collectionName, docs, delay = 900) {
  for (const [id, data] of docs) {
    try {
      await updateDocument(collectionName, id, data);
      console.log(`Documento actualizado: ${id}`);
    } catch (error) {
      console.error(error);
    }

    await sleep(delay);
  }
}

/**
 * Guarda un documento en Firestore.
 * @param {string} collectionName - El nombre de la colección.
 * @param {string} id - El ID del documento.
 * @param {object} data - Los datos que quieres guardar.
 * @param {object|null} converter - (opcional) Convertidor para transformar los datos.
 */
export const setDocument = async (
  collectionName,
  id,
  data,
  converter = null
) => {
  const docRef = converter
    ? doc(db, collectionName, id).withConverter(converter)
    : doc(db, collectionName, id);
  return setDoc(docRef, {
    ...data,
    createdAt: Timestamp.now(),
  });
};

/**
 * Elimina un documento de Firestore.
 * @param {string} collectionName - El nombre de la colección.
 * @param {string} id - El ID del documento a eliminar.
 */
export const deleteDocument = async (collectionName, id) => {
  const docRef = doc(db, collectionName, id);
  return deleteDoc(docRef);
};

/**
 * Elimina múltiples documentos de Firestore.
 * @param {string} collectionName - El nombre de la colección.
 * @param {Array<string>} ids - Los IDs de los documentos a eliminar.
 * @param {number} delay - (opcional) Tiempo en ms entre cada eliminación (por defecto 900ms)
 */
export const deleteMultiple = async (collectionName, ids, delay = 900) => {
  for (const id of ids) {
    try {
      await deleteDocument(collectionName, id);
      console.log(`Documento eliminado: ${id}`);
    } catch (error) {
      console.error(`Error al eliminar el documento ${id}:`, error);
    }

    await sleep(delay);
  }
};
